import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Loader, PageTitle, FiltersForm, ErrorMessage,
    TransactionsResume, TransactionsTable
} from '../../../components';
import Icon from '@mdi/react';
import { useDispatch, useSelector } from 'react-redux';
import { mdiCheck, mdiCloudUploadOutline, mdiSwapHorizontalBold, mdiCashPlus, mdiClose } from '@mdi/js';
import { useRouteMatch, Link } from 'react-router-dom';
import { Container, Row, Col, ButtonGroup, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { loadTransactions } from '../../../redux/modules/transactions';
import { askPaymentAction, resetAskPaymentAction } from '../../../redux/modules/payments';
import { toAccountIds } from '../../../components/FiltersForm/FiltersForm';
import './TransactionsList.css';

const TransactionsList = () => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const [mustShowModal, showModal] = useState(false);

    const { path }     = useRouteMatch();
    const user         = useSelector(state => state.auth.user);
    const filters      = useSelector(state => state.filters.values);

    const { isAskingPayment, askedPayment } = useSelector(state => state.payments);

    const {
        transactions, isLoading,
        hasLoadingError, loadingErrorMessage
    } = useSelector(state => state.transactions);
    const dueTotal = (transactions || [])
        .filter(t => !t.payment_id)
        .reduce((memo, t) => memo + t.net_amount, 0)
        .toFixed(2);
    const dueCurrency = transactions && transactions.length ? transactions[0].currency : 'CHF';

    useEffect(() => {
        if (!filters.from || !filters.to)
            return;
        dispatch(loadTransactions(filters));
    }, [dispatch, filters]);

    useEffect(() => {
        dispatch(resetAskPaymentAction());
    }, [dispatch]);

    const toggleModal = () => {
        showModal(!mustShowModal);
    };

    const selectedAccountIds = toAccountIds(filters.account_id);
    const canAskPayment = selectedAccountIds.length === 1;

    const askPayment = () => {
        if (!canAskPayment)
            return;
        dispatch(askPaymentAction(selectedAccountIds[0], filters, /*refresh*/false, (err, payment) => {
            showModal(false);
            dispatch(loadTransactions(filters));
        }));
    }

    const top_actions = user.isSuperAdmin() && (
        <div className="noPrint">
            <Link to={`${path}/new`}>
                <Button color="info">
                    <Icon path={mdiCashPlus} size={1} color="white" /> { t('transactions.add_title') }
                </Button>
            </Link>{' '}
            <Link to={`${path}/import`}>
                <Button color="info">
                    <Icon path={mdiCloudUploadOutline} size={1} color="white" /> { t('transactions.import_transactions') }
                </Button>
            </Link>
        </div>
    );

    return (
        <div className="TransactionsList">
            <PageTitle
                title="Transactions"
                icon={<Icon path={mdiSwapHorizontalBold} className="noPrint" color="white" />}
                actions={top_actions}
            />
            <FiltersForm />

            {hasLoadingError && (
                <ErrorMessage message={ loadingErrorMessage } />
            )}

            {isLoading && (
                <Loader />
            )}

            {!isLoading && (
                <div className="mt-3">
                    <TransactionsResume
                        transactions={transactions}
                        askPaymentButton={(
                            <Button color="info" disabled={ !canAskPayment } onClick={ toggleModal }>
                                <Icon path={ mdiCashPlus } color="white" size={1} />
                                {' '} { t('dashboard.btn_new_payment') }
                            </Button>
                        )}
                    />
                    {askedPayment && (
                        <div className="alert alert-success">
                            <Icon path={ mdiCheck } size={1} />&nbsp;
                            {t('transactions.payment_of_amount_has_been_asked', askedPayment)}
                        </div>
                    )}
                    <TransactionsTable
                        transactions={ transactions }
                    />
                </div>
            )}
            <Modal className="confirmModal" backdrop={ true } size={'lg'} isOpen={mustShowModal} toggle={ toggleModal }>
                <ModalHeader toggle={ toggleModal }>
                    <div>
                        <Icon path={ mdiCashPlus } color="white" size={1} />&nbsp;
                        {t('transactions.ask_for_mayment')}
                    </div>
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col sm={12}>
                            {isAskingPayment ? (
                                <Loader />
                            ) : (
                                <>
                                    <h4>{ t('transactions.are_you_sure_to_ask_payment') }</h4>
                                    <p className="mb-0">
                                        { t('transactions.ask_payment_due_amount', {
                                            amount: dueTotal,
                                            currency: dueCurrency
                                        }) }
                                    </p>
                                </>
                            )}
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Container>
                        <Row>
                            <Col sm={12} className="text-right">
                                <ButtonGroup>
                                    <Button color="secondary" disabled={isAskingPayment} onClick={ toggleModal }>
                                        <Icon path={ mdiClose } size={1} color="white" />
                                        { t('common.close') }
                                    </Button>
                                    <Button color="info" disabled={isAskingPayment} onClick={ askPayment }>
                                        <Icon path={ mdiCashPlus } size={1} color="white" />
                                        { t('transactions.ask_for_mayment') }
                                    </Button>
                                </ButtonGroup>
                            </Col>
                        </Row>
                    </Container>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default TransactionsList;
